<script lang="ts" setup>
defineProps({
  isMainSection: {
    default: false,
    type: Boolean,
  },
})

const { t } = useI18n()

const questions = computed(() => {
  return [
    {
      answer: t('questions.cancelOrUpgrade.answer'),
      icon: getIcon('cancelUpgrade'),
      question: t('questions.cancelOrUpgrade.question'),
    },
    {
      answer: t('questions.updatesFrequency.answer'),
      icon: getIcon('frequency'),
      question: t('questions.updatesFrequency.question'),
    },
    {
      answer: t('questions.legalAndGDPR.answer'),
      icon: getIcon('bookWithBookmark'),
      question: t('questions.legalAndGDPR.question'),
    },
    {
      answer: t('questions.dataUsage.answer'),
      icon: getIcon('computer'),
      question: t('questions.dataUsage.question'),
    },
    {
      answer: t('questions.costForSavingCompanyTwice.answer'),
      icon: getIcon('save'),
      question: t('questions.costForSavingCompanyTwice.question'),
    },
    {
      answer: t('questions.rateLimiting.answer'),
      icon: getIcon('rateLimit'),
      question: t('questions.rateLimiting.question'),
    },
  ]
})
</script>

<template>
  <LayoutSection>
    <template #left>
      <BaseHeading
        :legend="t('legend')"
        size="lg"
        :tag="isMainSection ? 'h1' : 'h2'"
        :title="t('title')"
      />
    </template>

    <template #right>
      <BaseQuestion
        v-for="(question, questionIndex) in questions"
        :key="questionIndex"
        :answer="question.answer"
        class="mx-auto mb-4 max-w-5xl last:mb-0"
        :icon="question.icon"
        :question="question.question"
      />
    </template>
  </LayoutSection>
</template>

<i18n lang="json">
{
  "en": {
    "legend": "We are here to help you",
    "questions": {
      "cancelOrUpgrade": {
        "answer": "Yes, you can choose to cancel or upgrade your subscription at any time.",
        "question": "Can I cancel or upgrade my subscription later?"
      },
      "costForSavingCompanyTwice": {
        "answer": "No, it won't. Besides, a company cannot be added a second time to a list it already belongs. Companies cannot be duplicated in the same list.",
        "question": "Will it cost 2 credits to save the same company twice?"
      },
      "dataUsage": {
        "answer": "You can utilize our data for any purpose you choose. We encourage you to leverage it for enhancing your products (such as autocomplete and onboarding), as well as for sales prospecting and market research.",
        "question": "How can I use your data?"
      },
      "legalAndGDPR": {
        "answer": "Yes, all data on The Companies API is public, GDPR-compliant and visible on the Internet by anyone. Our value is in designing algorithms so you don't have to do all these things manually.",
        "question": "Is it legal and GDPR compliant?"
      },
      "rateLimiting": {
        "answer": "Each plan comes with specific query limits for our API, which are detailed on our pricing page. If you need to make high-volume API requests (hundreds of queries per second), please refer to our pricing details or contact us.",
        "question": "Do you have rate limits?"
      },
      "updatesFrequency": {
        "answer": "Our database is continuously refreshed with new companies, and we remove those that no longer exist. You can also query any company, and we'll analyze it in real-time to give you the most current information.",
        "question": "How often do you update your database of companies?"
      }
    },
    "title": "Frequently Asked Questions"
  },
  "es": {
    "legend": "Estamos aquí para ayudarte",
    "questions": {
      "cancelOrUpgrade": {
        "answer": "Sí, puedes elegir cancelar o actualizar tu suscripción en cualquier momento.",
        "question": "¿Puedo cancelar o actualizar mi suscripción más adelante?"
      },
      "costForSavingCompanyTwice": {
        "answer": "No, no será así. Además, una empresa no puede ser añadida por segunda vez a una lista a la que ya pertenece. Las empresas no pueden duplicarse en la misma lista.",
        "question": "¿Costará 2 créditos guardar la misma empresa dos veces?"
      },
      "dataUsage": {
        "answer": "Puedes utilizar nuestros datos para cualquier propósito que elijas. Te animamos a aprovecharlos para mejorar tus productos (como autocompletado e incorporación), así como para prospección de ventas e investigación de mercado.",
        "question": "¿Cómo puedo usar sus datos?"
      },
      "legalAndGDPR": {
        "answer": "Sí, todos los datos en The Companies API son públicos, cumplen con el RGPD y son visibles en Internet para cualquiera. Nuestro valor está en diseñar algoritmos para que no tengas que hacer todas estas cosas manualmente.",
        "question": "¿Es legal y cumple con el RGPD?"
      },
      "rateLimiting": {
        "answer": "Cada plan viene con límites específicos de consultas para nuestra API, que se detallan en nuestra página de precios. Si necesitas hacer solicitudes API de alto volumen (cientos de consultas por segundo), consulta nuestros detalles de precios o contáctanos.",
        "question": "¿Tienen límites de tasa?"
      },
      "updatesFrequency": {
        "answer": "Nuestra base de datos se actualiza continuamente con nuevas empresas, y eliminamos aquellas que ya no existen. También puedes consultar cualquier empresa, y la analizaremos en tiempo real para darte la información más actualizada.",
        "question": "¿Con qué frecuencia actualizan su base de datos de empresas?"
      }
    },
    "title": "Preguntas frecuentes"
  },
  "fr": {
    "legend": "Nous sommes ici pour vous aider",
    "questions": {
      "cancelOrUpgrade": {
        "answer": "Oui, vous pouvez choisir d'annuler ou de mettre à jour votre abonnement à tout moment.",
        "question": "Puis-je annuler ou mettre à jour mon abonnement plus tard ?"
      },
      "costForSavingCompanyTwice": {
        "answer": "Non, ce ne sera pas le cas. De plus, une entreprise ne peut pas être ajoutée une seconde fois à une liste à laquelle elle appartient déjà. Les entreprises ne peuvent pas être dupliquées dans une même liste.",
        "question": "Cela coûtera-t-il 2 crédits de sauvegarder deux fois la même entreprise ?"
      },
      "dataUsage": {
        "answer": "Vous pouvez utiliser nos données pour n'importe quel usage. Nous vous encourageons à les exploiter pour améliorer vos produits (comme l'autocomplétion et l'intégration), ainsi que pour la prospection commerciale et les études de marché.",
        "question": "Comment puis-je utiliser vos données ?"
      },
      "legalAndGDPR": {
        "answer": "Oui, toutes les données sur The Companies API sont publiques, conformes au RGPD et visibles sur Internet par n'importe qui. Notre valeur réside dans la conception d'algorithmes pour que vous n'ayez pas à faire toutes ces choses manuellement.",
        "question": "Est-ce légal et conforme au RGPD ?"
      },
      "rateLimiting": {
        "answer": "Chaque plan comprend des limites spécifiques de requêtes pour notre API, qui sont détaillées sur notre page de tarification. Si vous avez besoin de faire des requêtes API en grand volume (des centaines de requêtes par seconde), veuillez consulter notre page de tarifs ou nous contacter.",
        "question": "Avez-vous un rate-limit ?"
      },
      "updatesFrequency": {
        "answer": "Notre base de données est actualisée en continue avec de nouvelles entreprises, et nous supprimons celles qui n'existent plus. Vous pouvez également interroger n'importe quelle entreprise, et nous l'analyserons en temps réel pour vous fournir les informations les plus récentes.",
        "question": "À quelle fréquence mettez-vous à jour la base de données d'entreprises ?"
      }
    },
    "title": "Questions fréquemment posées"
  }
}
</i18n>
